import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import Navigation from "./Navigation";
import Footer from "./Footer";
import { useLanguage } from "../context/LanguageContext";
import { archivedProjects, featuredProject } from "../data/projectsData";

const Projects = () => {
  const { language } = useLanguage();
  const location = useLocation();

  const t = (value) => {
    if (typeof value === "string") return value;
    return value?.[language] || value?.en || "";
  };

  useEffect(() => {
    if (location.state && location.state.projectId) {
      const element = document.getElementById(
        `project-${location.state.projectId}`
      );

      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }
    }
  }, [location]);

  const isExternalLink = (url) => /^https?:\/\//.test(url);

  const renderProjectLink = (project, className, children) => {
    if (isExternalLink(project.link)) {
      return (
        <a
          href={project.link}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
        >
          {children}
        </a>
      );
    }

    return (
      <Link to={project.link} className={className}>
        {children}
      </Link>
    );
  };

  const translations = {
    title: {
      en: "Projects",
      zh: "项目",
      ja: "プロジェクト",
    },
    featuredTitle: {
      en: "Featured System",
      zh: "核心系统",
      ja: "注目のシステム",
    },
    archiveTitle: {
      en: "Archive",
      zh: "归档项目",
      ja: "アーカイブ",
    },
    archiveIntro: {
      en: "Additional work across trading, security, research, and systems programming.",
      zh: "其他涵盖交易、安全、研究与系统方向的项目。",
      ja: "取引、セキュリティ、研究、システムプログラミングにまたがる追加プロジェクト。",
    },
    featuredLabel: {
      en: "Featured",
      zh: "Featured",
      ja: "Featured",
    },
  };

  const archiveVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: { delay: index * 0.05, duration: 0.4 },
    }),
  };

  return (
    <div className="bg-neutral-50 min-h-screen">
      <Navigation activeSection="projects" />
      <div className="pt-24 pb-12 px-6">
        <div className="container mx-auto max-w-6xl">
          <header className="max-w-4xl mx-auto text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-light text-neutral-800">
              {t(translations.title)}
            </h1>
          </header>

          {featuredProject && (
            <section className="mb-14">
              <div className="mb-5 flex items-center justify-between">
                <h2 className="text-2xl font-medium text-neutral-800">
                  {t(translations.featuredTitle)}
                </h2>
              </div>

              <div className="border-t border-neutral-200">
                <motion.article
                  id={`project-${featuredProject.id}`}
                  variants={archiveVariants}
                  initial="hidden"
                  animate="visible"
                  className="border-b border-neutral-200 py-5"
                >
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-6">
                    <div className="h-28 w-28 shrink-0 overflow-hidden bg-neutral-100 md:h-32 md:w-32">
                      <img
                        src={featuredProject.image}
                        alt={t(featuredProject.title)}
                        className="h-full w-full object-cover object-center"
                      />
                    </div>

                    <div className="min-w-0 flex-1 text-left">
                      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-400">
                        <span>{t(translations.featuredLabel)}</span>
                        <span>{t(featuredProject.date)}</span>
                      </div>

                      {renderProjectLink(
                        featuredProject,
                        "group inline-flex items-center gap-2 text-xl font-medium text-neutral-800 transition-colors hover:text-primary-600",
                        <>
                          <span>{t(featuredProject.title)}</span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 shrink-0"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </>
                      )}

                      <p className="mt-2 max-w-3xl text-sm leading-relaxed text-neutral-600">
                        {t(featuredProject.description)}
                      </p>

                      <p className="mt-3 text-xs leading-relaxed text-neutral-400">
                        {featuredProject.technologies.join(" / ")}
                      </p>
                    </div>
                  </div>
                </motion.article>
              </div>
            </section>
          )}

          <section className="pb-8">
            <div className="mb-8">
              <h2 className="text-2xl font-medium text-neutral-800 mb-3">
                {t(translations.archiveTitle)}
              </h2>
              <p className="text-base text-neutral-600">
                {t(translations.archiveIntro)}
              </p>
            </div>

            <div className="border-t border-neutral-200">
              {archivedProjects.map((project, index) => (
                <motion.article
                  key={project.id}
                  id={`project-${project.id}`}
                  custom={index}
                  variants={archiveVariants}
                  initial="hidden"
                  animate="visible"
                  className="border-b border-neutral-200 py-5"
                >
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-6">
                    <div className="w-full shrink-0 overflow-hidden bg-neutral-100 md:w-48">
                      <img
                        src={project.image}
                        alt={t(project.title)}
                        className="h-40 w-full object-cover object-center md:h-28"
                      />
                    </div>

                    <div className="min-w-0 flex-1 text-left">
                      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-400">
                        {project.context && <span>{t(project.context)}</span>}
                        <span>{t(project.date)}</span>
                      </div>

                      {renderProjectLink(
                        project,
                        "group inline-flex items-center gap-2 text-xl font-medium text-neutral-800 transition-colors hover:text-primary-600",
                        <>
                          <span>{t(project.title)}</span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 shrink-0"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </>
                      )}

                      <p className="mt-2 max-w-3xl text-sm leading-relaxed text-neutral-600">
                        {t(project.description)}
                      </p>

                      <p className="mt-3 text-xs leading-relaxed text-neutral-400">
                        {project.technologies.join(" / ")}
                      </p>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Projects;
